import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ReactComponent as KeyboardArrowDownIcon } from "../../asserts/logo/KeyboardArrowDown.svg";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import {
  DB_TYPE_OPTIONS,
  buildDbConfigPayload,
  dbFormCanSave,
  dbReportMatchesForm,
  normalizeDbType,
} from "../../utils/dbSurfaceForm";
import { runDataConnectivityCheck } from "../../services/readinessClient";
import RemoveDetailsButton from "./RemoveDetailsButton";

const F = INTAKE_FONT;
const PRIMARY = "#005BF0";

const fieldLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: "1px",
  width: "100%",
  height: 24,
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "16px",
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#707070",
};

const smallFieldLabelSx = {
  ...fieldLabelSx,
  height: 21,
  fontSize: "14px",
  lineHeight: "21px",
};

const requiredMarkSx = { color: "#DE350B" };

/* Required-field error state — red outline + message under the field (right aligned) */
const requiredErrorSx = {
  "& .MuiOutlinedInput-root.Mui-error fieldset": {
    borderColor: "#DE350B",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-error:hover fieldset": {
    borderColor: "#DE350B",
  },
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset": {
    borderColor: "#DE350B",
    borderWidth: "1px",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    width: "100%",
    height: "auto",
    m: 0,
    mt: "4px",
    textAlign: "right",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#DE350B",
  },
};

const fieldWrapperSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-input": {
      height: 24,
      boxSizing: "content-box",
      padding: "8px 10px",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: 0,
      color: "#000000",
      "&::placeholder": {
        color: "#707070",
        opacity: 1,
      },
    },
  },
  "& .MuiSelect-select": {
    height: "24px !important",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
    padding: "8px 40px 8px 10px !important",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
  },
  "& .MuiSelect-icon": {
    position: "absolute",
    right: 10,
    top: "50%",
    width: 24,
    height: 24,
    transform: "translateY(-50%)",
  },
  ...requiredErrorSx,
};

const testTableInputSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& .MuiOutlinedInput-root": {
    height: 86,
    minHeight: 86,
    boxSizing: "border-box",
    alignItems: "flex-start",
    p: 0,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    height: "70px !important",
    boxSizing: "content-box",
    overflow: "auto !important",
    padding: "8px 10px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    "&::placeholder": {
      color: "#707070",
      opacity: 1,
    },
  },
  "& .MuiFormHelperText-root": {
    width: "100%",
    height: 18,
    minHeight: 18,
    m: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#707070",
  },
};

function FieldLabel({ children, required = false, small = false }) {
  return (
    <Typography sx={small ? smallFieldLabelSx : fieldLabelSx} component="label">
      {required ? <Box component="span" sx={requiredMarkSx}>*</Box> : null}
      {children}
    </Typography>
  );
}

function isDbSuccess(report) {
  const s = String(report?.overall_status || "").toLowerCase();
  return s === "ready" || s === "pass" || s === "ready_with_warnings";
}

function dbTypeLabel(value) {
  const norm = normalizeDbType(value);
  if (norm === "mysql") return "MySQL";
  if (norm === "postgresql") return "PostgreSQL";
  return String(value || "").trim() || "database";
}

/** Digits only; drop letters/spaces/symbols/decimals; cap at 65535 (no format error message). */
function sanitizePortInput(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (Number(digits) <= 65535) return digits;
  let next = digits;
  while (next.length > 0 && Number(next) > 65535) {
    next = next.slice(0, -1);
  }
  return next;
}

export default function Gate1DbSurfacePanel({
  projectId,
  dbForm,
  onDbFormChange,
  dbCredentials,
  onDbCredentialsChange,
  dbReport,
  onDbReport,
  onSaveConnection,
  onRemoveDetails,
  saving = false,
  removing = false,
  saved = false,
  readOnly = false,
  errors = null,
}) {
  const fieldErrors = errors || {};
  /* Mandatory stars/blocking are off for this card until asked to restore. */
  const sectionRequired = false;
  const [probeRunning, setProbeRunning] = useState(false);
  const [probeError, setProbeError] = useState("");

  const canSave = dbFormCanSave(dbForm) && !readOnly && !saving;
  const canTest =
    canSave &&
    Boolean(String(dbCredentials.password || "").trim());
  const canRemove =
    Boolean(String(dbForm.dbType || "").trim()) ||
    Boolean(String(dbForm.host || "").trim()) ||
    Boolean(String(dbForm.database || "").trim()) ||
    Boolean(dbReport) ||
    saved;

  const handleDbTypeChange = (nextType) => {
    onDbFormChange({
      ...dbForm,
      dbType: nextType,
    });
  };

  const runProbe = async () => {
    const built = buildDbConfigPayload(dbForm, dbCredentials);
    if (built.error) {
      setProbeError(built.error);
      return;
    }

    try {
      setProbeRunning(true);
      setProbeError("");
      const report = await runDataConnectivityCheck(built.db_config, { projectId });
      onDbReport?.(report);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Database test failed.";
      setProbeError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setProbeRunning(false);
    }
  };

  const report = dbReportMatchesForm(dbReport, dbForm) ? dbReport : null;
  const previousReport = dbReport && !report ? dbReport : null;
  const testTableLen = String(dbForm.testTable || "").length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        border: "1px solid #E6E6E6",
        borderRadius: "8px",
        p: "24px",
        bgcolor: "#FFFFFF",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          width: "100%",
          m: 0,
          fontFamily: "Lato, sans-serif",
          fontWeight: 900,
          fontStyle: "normal",
          fontSize: "20px",
          lineHeight: "100%",
          letterSpacing: 0,
          color: "#383838",
        }}
      >
        Database connectivity
      </Typography>
      <Typography
        sx={{
          width: "100%",
          m: 0,
          fontFamily: "Lato, sans-serif",
          fontWeight: 400,
          fontStyle: "normal",
          fontSize: "14px",
          lineHeight: "100%",
          letterSpacing: 0,
          color: "#555555",
        }}
      >
        Test a normal SQL database (PostgreSQL or MySQL). Host, database, and username are saved
        with the connection. The password is required to test, but is not stored.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          columnGap: "24px",
          rowGap: "16px",
          width: "100%",
          minWidth: 0,
        }}
      >
        {/* Row 1 — type | name */}
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Database type</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            value={DB_TYPE_OPTIONS.includes(dbForm.dbType) ? dbForm.dbType : ""}
            onChange={(e) => handleDbTypeChange(e.target.value)}
            disabled={readOnly}
            error={Boolean(fieldErrors.dbType)}
            helperText={fieldErrors.dbType || ""}
            SelectProps={{
              displayEmpty: true,
              IconComponent: KeyboardArrowDownIcon,
              renderValue: (selected) => {
                if (!selected || !DB_TYPE_OPTIONS.includes(selected)) {
                  return (
                    <Typography
                      sx={{
                        color: "#707070",
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                      }}
                    >
                      Select database type
                    </Typography>
                  );
                }
                return selected;
              },
            }}
            sx={inputSx}
          >
            <MenuItem value="" disabled sx={{ display: "none" }}>
              Select database type
            </MenuItem>
            {DB_TYPE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Database name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter database name"
            value={dbForm.database || ""}
            onChange={(e) => onDbFormChange({ ...dbForm, database: e.target.value })}
            disabled={readOnly}
            inputProps={{ autoComplete: "off", name: "db-name-readiness" }}
            error={Boolean(fieldErrors.database)}
            helperText={fieldErrors.database || ""}
            sx={inputSx}
          />
        </Box>

        {/* Row 2 — host | port */}
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Database host</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter database type"
            value={dbForm.host || ""}
            onChange={(e) => onDbFormChange({ ...dbForm, host: e.target.value })}
            disabled={readOnly}
            inputProps={{ autoComplete: "off", name: "db-host-readiness" }}
            error={Boolean(fieldErrors.host)}
            helperText={fieldErrors.host || ""}
            sx={inputSx}
          />
        </Box>
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Port</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g., 5432"
            value={dbForm.port || ""}
            onChange={(e) =>
              onDbFormChange({ ...dbForm, port: sanitizePortInput(e.target.value) })
            }
            disabled={readOnly}
            inputProps={{
              autoComplete: "off",
              name: "db-port-readiness",
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            error={Boolean(fieldErrors.port)}
            helperText={fieldErrors.port || ""}
            sx={inputSx}
          />
        </Box>

        {/* Row 3 — username | password */}
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Username</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter username"
            value={dbForm.username || ""}
            onChange={(e) => onDbFormChange({ ...dbForm, username: e.target.value })}
            disabled={readOnly}
            inputProps={{ autoComplete: "off", name: "db-username-readiness" }}
            error={Boolean(fieldErrors.username)}
            helperText={fieldErrors.username || ""}
            sx={inputSx}
          />
        </Box>
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Password</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="password"
            placeholder="Enter password"
            value={dbCredentials.password || ""}
            onChange={(e) => onDbCredentialsChange({ ...dbCredentials, password: e.target.value })}
            disabled={readOnly}
            inputProps={{ autoComplete: "new-password", name: "db-password-readiness" }}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password || ""}
            sx={inputSx}
          />
        </Box>

        {/* Row 4 — test table full width */}
        <Box sx={{ ...fieldWrapperSx, gridColumn: "1 / -1" }}>
          <FieldLabel small>Test table</FieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="e.g., users"
            value={dbForm.testTable || ""}
            onChange={(e) => onDbFormChange({ ...dbForm, testTable: e.target.value.slice(0, 240) })}
            disabled={readOnly}
            inputProps={{ maxLength: 240, autoComplete: "off", name: "db-test-table-readiness" }}
            helperText={
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  width: "100%",
                  height: 18,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    flex: "1 1 auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  If provided, we&apos;ll check the table after connecting.
                </span>
                <span style={{ flexShrink: 0, textAlign: "right" }}>{testTableLen}/240</span>
              </Box>
            }
            FormHelperTextProps={{ sx: { m: 0 } }}
            sx={testTableInputSx}
          />
        </Box>
      </Box>

      {!readOnly && (
        <Box
          sx={{
            width: "100%",
            minHeight: 32,
            height: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            disabled={!canSave}
            onClick={() => canSave && onSaveConnection?.()}
            sx={{
              width: "fit-content",
              minWidth: 140,
              height: 32,
              boxSizing: "border-box",
              p: "4px 16px",
              textTransform: "none",
              fontWeight: 700,
              fontFamily: "Lato, sans-serif",
              fontSize: "13px",
              lineHeight: "24px",
              border: "1px solid #005BF0",
              color: PRIMARY,
              borderRadius: "4px",
              bgcolor: "#FFFFFF",
              "&.Mui-disabled": {
                border: "1px solid #E6E6E6",
                bgcolor: "#F5F5F5",
                color: "#BBBBBB",
              },
            }}
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save details"}
          </Button>
          <RemoveDetailsButton
            onClick={() => onRemoveDetails?.()}
            disabled={!canRemove || saving || probeRunning}
            removing={removing}
          />
          <Button
            variant="outlined"
            disabled={!canTest || probeRunning}
            onClick={runProbe}
            startIcon={
              probeRunning ? <CircularProgress size={16} sx={{ color: PRIMARY }} /> : undefined
            }
            sx={{
              width: "fit-content",
              minWidth: 179,
              height: 32,
              boxSizing: "border-box",
              p: "4px 16px",
              gap: "4px",
              textTransform: "none",
              fontWeight: 700,
              fontFamily: "Lato, sans-serif",
              fontSize: "13px",
              lineHeight: "24px",
              letterSpacing: 0,
              border: "1px solid #005BF0",
              color: PRIMARY,
              borderRadius: "4px",
              bgcolor: "#FFFFFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
              "&:hover": {
                border: "1px solid #005BF0",
                bgcolor: "#FFFFFF",
                backgroundImage:
                  "linear-gradient(0deg, rgba(0, 91, 240, 0.08), rgba(0, 91, 240, 0.08))",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
              },
              "&:focus-visible, &.Mui-focusVisible": {
                border: "2px solid #005FCC",
                bgcolor: "#FFFFFF",
                backgroundImage: "none",
                outline: "none",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
              },
              "& .MuiTouchRipple-ripplePulsate": {
                display: "none",
              },
              "&:active": {
                border: "1px solid #005BF0",
                bgcolor: "#FFFFFF",
                backgroundImage: "none",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
              },
              "&.Mui-disabled": {
                border: "1px solid #E6E6E6",
                bgcolor: "#F5F5F5",
                backgroundImage: "none",
                color: "#BBBBBB",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            {probeRunning ? "Testing…" : "Test database connection"}
          </Button>
        </Box>
      )}

      {previousReport && !report && (
        <Alert severity="info" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
          Previous {dbTypeLabel(previousReport.db_type || previousReport.dbType)} result is stale
          and is not shown as the current database result. Run a new connection test for{" "}
          {DB_TYPE_OPTIONS.includes(dbForm.dbType) ? dbForm.dbType : "the selected database"}.
        </Alert>
      )}

      {probeError && (
        <Alert severity="warning" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
          {probeError}
        </Alert>
      )}

      {report && !probeError && report.generated_at && (
        <Alert
          severity={isDbSuccess(report) ? "success" : "error"}
          sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}
        >
          {report.notes ||
            (isDbSuccess(report) ? "Database connection successful." : "Database connection failed.")}
          {report.latency_ms != null ? ` (${report.latency_ms}ms)` : ""}
          {report.table_check?.passed && report.table_check.row_count != null
            ? ` · ${report.table_check.table}: ${report.table_check.row_count} row(s)`
            : ""}
        </Alert>
      )}

      {report?.table_check && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={report.table_check.passed ? "Table readable" : "Table check failed"}
            sx={{
              height: 22,
              fontWeight: 700,
              bgcolor: report.table_check.passed ? INTAKE_COLORS.green : INTAKE_COLORS.red,
              color: "#fff",
            }}
          />
        </Stack>
      )}
    </Box>
  );
}
